export const MAX_RECORDING_SECONDS = 10 * 60;

export interface MediaRecorderSupport {
  isTypeSupported?: (mimeType: string) => boolean;
}

function firstSupported(recorderApi: MediaRecorderSupport, mimeTypes: string[]): string {
  if (typeof recorderApi.isTypeSupported !== "function") return "";
  return mimeTypes.find((mimeType) => recorderApi.isTypeSupported!(mimeType)) ?? "";
}

export function chooseRecordingMimeType(
  recorderApi: MediaRecorderSupport | undefined,
  userAgent = "",
): string {
  if (!recorderApi || typeof recorderApi.isTypeSupported !== "function") return "";

  const isAppleMobile = /iPhone|iPad|iPod/i.test(userAgent);
  if (isAppleMobile) {
    return firstSupported(recorderApi, [
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/webm;codecs=opus",
      "audio/webm",
    ]);
  }

  // Chromium can accept generic audio/mp4 but currently produce Opus inside it.
  // Prefer its native, accurately labelled WebM/Opus output for reliable playback.
  return firstSupported(recorderApi, [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ]);
}

export function extensionForAudioMimeType(mimeType: string): string {
  const normalized = mimeType.toLowerCase().split(";")[0].trim();
  if (normalized === "audio/mp4" || normalized === "audio/aac") return "m4a";
  if (normalized === "audio/ogg") return "ogg";
  if (normalized === "audio/webm") return "webm";
  return "audio";
}

export function formatRecordingDuration(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60).toString();
  const remainder = (safeSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
}

export function microphonePermissionHelp(userAgent = ""): string {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    const browser = /CriOS/i.test(userAgent) ? "Chrome" : "Safari";
    return `Microphone access is off. Open Settings, find ${browser}, tap Microphone, and allow access. Return here and reload the page; if the browser asks again, tap Allow.`;
  }
  if (/Android/i.test(userAgent)) {
    return "Microphone access is off. In Chrome, tap the site controls beside the address, open Permissions, and allow Microphone. Then return here and try again.";
  }
  return "Microphone access is off. Allow microphone access in this site’s browser settings, then try again.";
}

export function createRecordingPath(
  userId: string,
  pageNumber: string,
  mimeType: string,
  now = new Date(),
): string {
  const timestamp = now.toISOString().replace(/[:.]/g, "-");
  return `${userId}/${pageNumber}/${timestamp}.${extensionForAudioMimeType(mimeType)}`;
}
